/**
 * Event Food Calculator — TypeScript entry point.
 * Handles scroll-to-results and hidden-iframe print flow.
 */
import { initIframePrint } from './shared/iframe-print';

/**
 * Smooth-scroll to the results section.
 */
function scrollToResults(): void {
    const resultsEl = document.getElementById('efc-results');
    if (resultsEl) {
        window.requestAnimationFrame(() => {
            resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }
}

/**
 * Load sample event data into the form.
 */
function loadSampleData(): void {
    const fields: Record<string, string> = {
        'Input_NumberOfGuests': '75',
        'Input_EventDuration': '4',
    };

    for (const id in fields) {
        const el = document.getElementById(id) as HTMLInputElement | null;
        if (el) {
            el.value = fields[id];
        }
    }

    const selects: Record<string, string> = {
        'Input_EventType': 'Buffet',
        'Input_AppetiteLevel': 'Average',
    };

    for (const id in selects) {
        const el = document.getElementById(id) as HTMLSelectElement | null;
        if (el) {
            el.value = selects[id];
        }
    }

    const checkboxes: Record<string, boolean> = {
        'Input_IncludeAppetizers': true,
        'Input_IncludeDessert': true,
        'Input_IncludeBeverages': true,
        'Input_IncludeAlcohol': false,
    };

    for (const id in checkboxes) {
        const el = document.getElementById(id) as HTMLInputElement | null;
        if (el) {
            el.checked = checkboxes[id];
        }
    }

    clearAllValidation();
}

/**
 * Attach input listeners to validated fields so errors clear dynamically.
 */
function attachValidationClearing(): void {
    const validated = [
        { input: 'Input_NumberOfGuests', msg: 'Input.NumberOfGuests' },
        { input: 'Input_EventDuration', msg: 'Input.EventDuration' },
    ];

    validated.forEach(({ input, msg }) => {
        const inputEl = document.getElementById(input) as HTMLInputElement | null;
        const spanEl = document.querySelector(`[data-valmsg-for="${msg}"]`) as HTMLElement | null;
        if (inputEl && spanEl) {
            inputEl.addEventListener('input', () => {
                if (inputEl.value.trim() !== '') {
                    spanEl.textContent = '';
                    spanEl.classList.remove('field-validation-error');
                    spanEl.classList.add('field-validation-valid');
                    dismissTopAlert();
                }
            });
        }
    });
}

/**
 * Clear all visible validation messages.
 */
function clearAllValidation(): void {
    document.querySelectorAll('.field-validation-error').forEach((el) => {
        el.textContent = '';
        el.classList.remove('field-validation-error');
        el.classList.add('field-validation-valid');
    });
    dismissTopAlert();
}

/**
 * Remove the top validation alert if no field errors remain.
 */
function dismissTopAlert(): void {
    const remaining = document.querySelectorAll('.field-validation-error');
    if (remaining.length === 0) {
        const alert = document.querySelector('#efcForm > .alert-warning') as HTMLElement | null;
        if (alert) {
            alert.remove();
        }
    }
}

/**
 * Initialise when the DOM is ready.
 */
function init(): void {
    if (document.getElementById('efc-results')) {
        scrollToResults();
    }

    initIframePrint({
        formId: 'efcPrintForm',
        iframeId: 'efcPrintFrame',
        buttonId: 'efcPrintBtn',
    });

    const loadSampleBtn = document.getElementById('efcLoadSampleBtn');
    if (loadSampleBtn) {
        loadSampleBtn.addEventListener('click', loadSampleData);
    }

    attachValidationClearing();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
